/**
 * Google Analytics / GTM Event Constants
 * Centralized event configurations for consistent tracking
 */

// Event Categories
export const GA_CATEGORIES = {
    QUOTE_PAGE: "Quote Page",
    USE_QUOTE: "Use Quote",
    INVITATION_PAGE: "Invitation Page",
    REVIEW_PAGE: "Review Information Page",
    PAYMENT: "Payment",
    DECISION: "Decision",
    SAVE_AND_CONTINUE: "Save And Continue",
} as const;

// Event Names
export const GA_EVENTS = {
    QUOTE_INTERACTION: "quote_interaction",
    INVITATION_INTERACTION: "invitation_interaction",
    REVIEW_INTERACTION: "review_interaction",
    PAYMENT_INTERACTION: "payment_interaction",
    POLICY_DECISION: "policy_decision",
    SAVE_CONTINUE_INTERACTION: "save_continue_interaction",
} as const;

// Event Actions
export const GA_ACTIONS = {
    GET_QUOTES: "Get Quotes",
    INPUT_CHANGE: "Input Change",
    SAVE_QUOTE: "Save Quote",
    STATE_INPUT_ATTEMPT: "State Input Attempt",
    CLEAR_QUOTE: "Clear Quote",
    SUBMIT: "Submit",
    VIEW: "View",
    ABANDON: "Abandon",
    SUBMIT_ATTEMPT: "Submit Attempt",
    OPEN_MODAL: "Open Modal",
    CLOSE_MODAL: "Close Modal",
    EDIT: "Edit",
} as const;

// Event Labels
export const GA_LABELS = {
    // Quote Labels
    INVALID_QUOTES: "Invalid Quotes",
    FETCH_QUOTES_ERROR: "Fetch Quotes Error",
    EMPTY_OFFER: "Empty Offer",
    SAVE_QUOTE: "Save Quote",
    STATE_NOT_AVAILABLE: "State Not Available",
    CLEAR_QUOTE: "Clear Quote",
    APPLY_NOW: "Apply Now",

    // Invitation Labels
    WHERE_INVITATION_CODE_MODAL: "WhereInvitationCodeModal",
    EMPTY_CODE: "Empty Code",
    INVALID_LENGTH: "Invalid Length",
    INVALID_CODE_API: "Invalid Code - API Rejected",
    INVALID_PURL: "Invalid PURL - API Rejected",
    VALID_CODE_SUBMITTED: "Valid Code Submitted",

    // Review Page Labels
    EDIT_PAYMENT_INFORMATION: "Edit Payment Information",
    EDIT_POLICY_BENEFICIARY: "Edit Policy Beneficiary",
    EDIT_CONTACT_INFORMATION: "Edit Contact Information",
    EDIT_STATEMENT_OF_HEALTH: "Edit Statement Of Health",
    EDIT_SECONDARY_ADDRESSEE: "Edit Secondary Addressee",
    EDIT_COVERAGE_REPLACEMENT: "Edit Coverage Replacement",

    // Payment Labels
    PAYMENT_CC_SUCCESS: "Credit Card Payment Success",
    PAYMENT_CC_FAILED: "Credit Card Payment Failed",
    PAYMENT_BANK_SUCCESS: "Bank Transfer Payment Success",
    PAYMENT_BANK_FAILED: "Bank Transfer Payment Failed",

    // Decision Labels
    APPROVED: "Approved",
    DECLINED: "Declined",
    NEEDS_CLOSER_LOOK: "Needs Closer Look",
    ALREADY_USED: "Already Used",
    INCOMPLETE: "Incomplete",
    APPROVED_RESUMED: "Approved (Resumed)",
    DECLINED_RESUMED: "Declined (Resumed)",
    NEEDS_CLOSER_LOOK_RESUMED: "Needs Closer Look (Resumed)",
    ALREADY_USED_RESUMED: "Already Used (Resumed)",
    INCOMPLETE_RESUMED: "Incomplete (Resumed)",
    SYSTEM_ERROR: "System Error",

    // Save & Continue Labels
    SESSION_CREATED: "Session Created",
    RESUMED: "Resumed",
    EXPIRED_PAGE_VIEWED: "Expired Page Viewed",
    AUTH_FAILED_PAGE_VIEWED: "Auth Failed Page Viewed",
    SUCCESS: "Success",
    INVALID_IDENTITY: "Invalid Identity",
    NOT_FOUND: "Not Found",
    EXPIRED: "Expired",
    LOCKED: "Locked",
    ERROR: "Error",
    TAB_CLOSE: "Tab Close",
    SESSION_TIMEOUT: "Session Timeout",
} as const;

export const GA_DEDUP_KEYS = {
    // Save & Continue
    RESUMED: "save-continue:resumed",
    ATTEMPT_SUCCESS: "save-continue:attempt:success",
    ATTEMPT_INVALID_IDENTITY: "save-continue:attempt:invalid-identity",
    ATTEMPT_NOT_FOUND: "save-continue:attempt:not-found",
    ATTEMPT_EXPIRED: "save-continue:attempt:expired",
    ATTEMPT_LOCKED: "save-continue:attempt:locked",
    ATTEMPT_ERROR: "save-continue:attempt:error",
    VIEW_EXPIRED_PAGE: "save-continue:view:expired-page",
    VIEW_AUTH_FAILED_PAGE: "save-continue:view:auth-failed-page",
    ABANDON_TAB_CLOSE: "save-continue:abandon:tab-close",
    ABANDON_SESSION_TIMEOUT: "save-continue:abandon:session-timeout",
    SESSION_CREATED: "save-continue:session-created",
} as const;

// Predefined Event Configurations
export const GA_EVENT_CONFIGS = {
    // Quote Events
    QUOTE_INVALID: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.USE_QUOTE,
        action: GA_ACTIONS.GET_QUOTES,
        label: GA_LABELS.INVALID_QUOTES,
    },
    QUOTE_FETCH_ERROR: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.USE_QUOTE,
        action: GA_ACTIONS.GET_QUOTES,
        label: GA_LABELS.FETCH_QUOTES_ERROR,
    },
    QUOTE_EMPTY_OFFER: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.QUOTE_PAGE,
        action: GA_ACTIONS.INPUT_CHANGE,
        label: GA_LABELS.EMPTY_OFFER,
    },
    QUOTE_SAVE: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.QUOTE_PAGE,
        action: GA_ACTIONS.SAVE_QUOTE,
        label: GA_LABELS.SAVE_QUOTE,
    },
    QUOTE_STATE_NOT_AVAILABLE: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.QUOTE_PAGE,
        action: GA_ACTIONS.STATE_INPUT_ATTEMPT,
        label: GA_LABELS.STATE_NOT_AVAILABLE,
    },
    QUOTE_CLEAR: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.QUOTE_PAGE,
        action: GA_ACTIONS.CLEAR_QUOTE,
        label: GA_LABELS.CLEAR_QUOTE,
    },
    QUOTE_APPLY_NOW: {
        event: GA_EVENTS.QUOTE_INTERACTION,
        category: GA_CATEGORIES.QUOTE_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.APPLY_NOW,
    },

    // Invitation Events
    INVITATION_MODAL_OPEN: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.OPEN_MODAL,
        label: GA_LABELS.WHERE_INVITATION_CODE_MODAL,
    },
    INVITATION_MODAL_CLOSE: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.CLOSE_MODAL,
        label: GA_LABELS.WHERE_INVITATION_CODE_MODAL,
    },
    INVITATION_EMPTY_CODE: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.SUBMIT_ATTEMPT,
        label: GA_LABELS.EMPTY_CODE,
    },
    INVITATION_INVALID_LENGTH: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.SUBMIT_ATTEMPT,
        label: GA_LABELS.INVALID_LENGTH,
    },
    INVITATION_SUBMIT_SUCCESS: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.VALID_CODE_SUBMITTED,
    },
    INVITATION_API_ERROR: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.SUBMIT_ATTEMPT,
        label: GA_LABELS.INVALID_CODE_API,
    },
    INVITATION_PURL_ERROR: {
        event: GA_EVENTS.INVITATION_INTERACTION,
        category: GA_CATEGORIES.INVITATION_PAGE,
        action: GA_ACTIONS.SUBMIT_ATTEMPT,
        label: GA_LABELS.INVALID_PURL,
    },

    // Review Page Events
    REVIEW_EDIT_PAYMENT: {
        event: GA_EVENTS.REVIEW_INTERACTION,
        category: GA_CATEGORIES.REVIEW_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EDIT_PAYMENT_INFORMATION,
    },
    REVIEW_EDIT_BENEFICIARY: {
        event: GA_EVENTS.REVIEW_INTERACTION,
        category: GA_CATEGORIES.REVIEW_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EDIT_POLICY_BENEFICIARY,
    },
    REVIEW_EDIT_CONTACT: {
        event: GA_EVENTS.REVIEW_INTERACTION,
        category: GA_CATEGORIES.REVIEW_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EDIT_CONTACT_INFORMATION,
    },
    REVIEW_EDIT_HEALTH: {
        event: GA_EVENTS.REVIEW_INTERACTION,
        category: GA_CATEGORIES.REVIEW_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EDIT_STATEMENT_OF_HEALTH,
    },
    REVIEW_EDIT_SECONDARY: {
        event: GA_EVENTS.REVIEW_INTERACTION,
        category: GA_CATEGORIES.REVIEW_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EDIT_SECONDARY_ADDRESSEE,
    },
    REVIEW_EDIT_COVERAGE: {
        event: GA_EVENTS.REVIEW_INTERACTION,
        category: GA_CATEGORIES.REVIEW_PAGE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EDIT_COVERAGE_REPLACEMENT,
    },

    // Payment Events
    PAYMENT_CC_SUCCESS: {
        event: GA_EVENTS.PAYMENT_INTERACTION,
        category: GA_CATEGORIES.PAYMENT,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.PAYMENT_CC_SUCCESS,
    },
    PAYMENT_CC_FAILED: {
        event: GA_EVENTS.PAYMENT_INTERACTION,
        category: GA_CATEGORIES.PAYMENT,
        action: GA_ACTIONS.SUBMIT_ATTEMPT,
        label: GA_LABELS.PAYMENT_CC_FAILED,
    },
    PAYMENT_BANK_SUCCESS: {
        event: GA_EVENTS.PAYMENT_INTERACTION,
        category: GA_CATEGORIES.PAYMENT,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.PAYMENT_BANK_SUCCESS,
    },
    PAYMENT_BANK_FAILED: {
        event: GA_EVENTS.PAYMENT_INTERACTION,
        category: GA_CATEGORIES.PAYMENT,
        action: GA_ACTIONS.SUBMIT_ATTEMPT,
        label: GA_LABELS.PAYMENT_BANK_FAILED,
    },

    // Policy Decision Events (regular vs resumed cohort; POLICY_SYSTEM_ERROR is not a decision outcome)
    POLICY_APPROVED_REGULAR: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.APPROVED,
    },
    POLICY_APPROVED_RESUMED: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.APPROVED_RESUMED,
    },
    POLICY_DECLINED_REGULAR: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.DECLINED,
    },
    POLICY_DECLINED_RESUMED: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.DECLINED_RESUMED,
    },
    POLICY_NEEDS_CLOSER_LOOK_REGULAR: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.NEEDS_CLOSER_LOOK,
    },
    POLICY_NEEDS_CLOSER_LOOK_RESUMED: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.NEEDS_CLOSER_LOOK_RESUMED,
    },
    POLICY_ALREADY_USED_REGULAR: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.ALREADY_USED,
    },
    POLICY_ALREADY_USED_RESUMED: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.ALREADY_USED_RESUMED,
    },
    POLICY_INCOMPLETE_REGULAR: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.INCOMPLETE,
    },
    POLICY_INCOMPLETE_RESUMED: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.INCOMPLETE_RESUMED,
    },
    POLICY_SYSTEM_ERROR: {
        event: GA_EVENTS.POLICY_DECISION,
        category: GA_CATEGORIES.DECISION,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.SYSTEM_ERROR,
    },

    // Save & Continue Events
    SAVE_CONTINUE_SESSION_CREATED: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.SESSION_CREATED,
    },
    SAVE_CONTINUE_RESUMED: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.RESUMED,
        dedupKey: GA_DEDUP_KEYS.RESUMED,
    },
    SAVE_CONTINUE_EXPIRED_PAGE_VIEWED: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.VIEW,
        label: GA_LABELS.EXPIRED_PAGE_VIEWED,
    },
    SAVE_CONTINUE_AUTH_FAILED_PAGE_VIEWED: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.VIEW,
        label: GA_LABELS.AUTH_FAILED_PAGE_VIEWED,
    },
    SAVE_CONTINUE_ATTEMPT_SUCCESS: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.SUCCESS,
        dedupKey: GA_DEDUP_KEYS.ATTEMPT_SUCCESS,
    },
    SAVE_CONTINUE_ATTEMPT_INVALID_IDENTITY: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.INVALID_IDENTITY,
        dedupKey: GA_DEDUP_KEYS.ATTEMPT_INVALID_IDENTITY,
    },
    SAVE_CONTINUE_ATTEMPT_NOT_FOUND: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.NOT_FOUND,
        dedupKey: GA_DEDUP_KEYS.ATTEMPT_NOT_FOUND,
    },
    SAVE_CONTINUE_ATTEMPT_EXPIRED: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.EXPIRED,
        dedupKey: GA_DEDUP_KEYS.ATTEMPT_EXPIRED,
    },
    SAVE_CONTINUE_ATTEMPT_LOCKED: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.LOCKED,
        dedupKey: GA_DEDUP_KEYS.ATTEMPT_LOCKED,
    },
    SAVE_CONTINUE_ATTEMPT_ERROR: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.SUBMIT,
        label: GA_LABELS.ERROR,
        dedupKey: GA_DEDUP_KEYS.ATTEMPT_ERROR,
    },
    SAVE_CONTINUE_ABANDON_TAB_CLOSE: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.ABANDON,
        label: GA_LABELS.TAB_CLOSE,
        dedupKey: GA_DEDUP_KEYS.ABANDON_TAB_CLOSE,
    },
    SAVE_CONTINUE_ABANDON_SESSION_TIMEOUT: {
        event: GA_EVENTS.SAVE_CONTINUE_INTERACTION,
        category: GA_CATEGORIES.SAVE_AND_CONTINUE,
        action: GA_ACTIONS.ABANDON,
        label: GA_LABELS.SESSION_TIMEOUT,
    },
} as const;

/** Maps policy decision + cohort to GTM config. `exception` mirrors `refertounderwriter` (same closer-look outcome). */
export const POLICY_DECISION_CONFIGS = {
    approved: {
        regular: GA_EVENT_CONFIGS.POLICY_APPROVED_REGULAR,
        resumed: GA_EVENT_CONFIGS.POLICY_APPROVED_RESUMED,
    },
    declined: {
        regular: GA_EVENT_CONFIGS.POLICY_DECLINED_REGULAR,
        resumed: GA_EVENT_CONFIGS.POLICY_DECLINED_RESUMED,
    },
    alreadyused: {
        regular: GA_EVENT_CONFIGS.POLICY_ALREADY_USED_REGULAR,
        resumed: GA_EVENT_CONFIGS.POLICY_ALREADY_USED_RESUMED,
    },
    refertounderwriter: {
        regular: GA_EVENT_CONFIGS.POLICY_NEEDS_CLOSER_LOOK_REGULAR,
        resumed: GA_EVENT_CONFIGS.POLICY_NEEDS_CLOSER_LOOK_RESUMED,
    },
    incomplete: {
        regular: GA_EVENT_CONFIGS.POLICY_INCOMPLETE_REGULAR,
        resumed: GA_EVENT_CONFIGS.POLICY_INCOMPLETE_RESUMED,
    },
    exception: {
        regular: GA_EVENT_CONFIGS.POLICY_NEEDS_CLOSER_LOOK_REGULAR,
        resumed: GA_EVENT_CONFIGS.POLICY_NEEDS_CLOSER_LOOK_RESUMED,
    },
} as const;
